import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, map, take, of, switchMap } from 'rxjs';
import { CommonService } from '../shared/services/common.service';
import { environment } from '../../environments/environment';
import { AngularFireAuth } from '@angular/fire/compat/auth';

interface Version {
  current_version: string;
  app_type: string;
}

@Injectable({
  providedIn: 'root'
})
export class VersionControlGuard implements CanActivate {

  constructor(
    private commonService: CommonService,
    private router: Router,
    private afAuth: AngularFireAuth
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    
    // If we're already on the new-version route, allow it
    if (state.url === '/new-version') {
      return true;
    }

    // Skip version check in non-production environments (local dev, preview channels)
    if (!environment.production) {
      return true;
    }

    // Check if user is authenticated
    return this.afAuth.authState.pipe(
      take(1),
      switchMap(user => {
        // If not authenticated, redirect to login
        if (!user) {
            return of(this.router.createUrlTree(['/auth']));
        }
          
        // User is authenticated, now check version
        return this.commonService.getCurrentVersion().pipe(
          take(1),
          map((versions: Version[]) => {
            if (versions && versions.length > 0) {
              const serverVersion = versions[0].current_version;
              const currentVersion = environment.version;
                
              // Compare versions using semantic versioning
              const isNewer = this.isNewerVersion(serverVersion, currentVersion);
              
              if (isNewer) {
                return this.router.createUrlTree(['/new-version']);
              }
            }
            return true;
          })
        );
      })
    );
  }

  /**
   * Compares two version strings to determine if the first version is newer than the second
   * @param serverVersion The version from the server
   * @param localVersion The local version
   * @returns true if serverVersion is newer than localVersion
   */
  isNewerVersion(serverVersion: string, localVersion: string): boolean {
    // Split version strings into parts (major.minor.patch)
    const serverParts = serverVersion.split('.').map(part => parseInt(part, 10));
    const localParts = localVersion.split('.').map(part => parseInt(part, 10));
    
    // Compare major version
    if (serverParts[0] > localParts[0]) return true;
    if (serverParts[0] < localParts[0]) return false;
    
    // If major versions are equal, compare minor version
    if (serverParts[1] > localParts[1]) return true;
    if (serverParts[1] < localParts[1]) return false;
    
    // If minor versions are equal, compare patch version
    if (serverParts[2] > localParts[2]) return true;
    
    // If we get here, the versions are either equal or local is newer
    return false;
  }
}
